'use client';

/**
 * WorkflowStep Component
 *
 * A step indicator for workflow/journey visualizations.
 * Features an icon in a rounded container with optional status dot.
 */

import Box, { type BoxProps } from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import type React from 'react';
import { forwardRef } from 'react';
import { baseColors } from '../../theme/palette';
import { glowEffects } from '../../theme/shadows';
import { StatusDot, type StatusDotColor } from '../StatusDot';

// ============================================================================
// Types
// ============================================================================

export type WorkflowStepVariant = 'automated' | 'manual' | 'external' | 'neutral';

export interface WorkflowStepProps extends Omit<BoxProps, 'title'> {
  /**
   * Icon to display
   */
  icon: React.ReactNode;

  /**
   * Step title
   */
  title: string;

  /**
   * Subtitle (e.g., time estimate)
   */
  subtitle?: string;

  /**
   * Step variant determines styling
   * @default 'neutral'
   */
  variant?: WorkflowStepVariant;

  /**
   * Status indicator color
   */
  status?: StatusDotColor;

  /**
   * Whether status is pulsing (active)
   */
  statusPulse?: boolean;

  /**
   * Size of the icon container
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Enable hover glow effect
   * @default true
   */
  glowOnHover?: boolean;

  /**
   * Click handler
   */
  onClick?: () => void;

  /**
   * Whether this step is active
   */
  active?: boolean;
}

// ============================================================================
// Variant Colors
// ============================================================================

const getVariantStyles = (variant: WorkflowStepVariant, isDark: boolean) => {
  const variants = {
    automated: {
      background: isDark ? 'rgba(15, 181, 174, 0.12)' : 'rgba(15, 181, 174, 0.08)',
      border: isDark ? 'rgba(15, 181, 174, 0.25)' : 'rgba(15, 181, 174, 0.2)',
      iconColor: baseColors.teal[isDark ? 400 : 500],
      glow: glowEffects.teal.subtle,
    },
    manual: {
      background: isDark ? 'rgba(245, 158, 11, 0.12)' : 'rgba(245, 158, 11, 0.08)',
      border: isDark ? 'rgba(245, 158, 11, 0.25)' : 'rgba(245, 158, 11, 0.2)',
      iconColor: baseColors.warning[isDark ? 400 : 500],
      glow: `0 0 20px rgba(245, 158, 11, 0.15)`,
    },
    external: {
      background: isDark ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)',
      border: isDark ? 'rgba(59, 130, 246, 0.25)' : 'rgba(59, 130, 246, 0.2)',
      iconColor: baseColors.info[isDark ? 400 : 500],
      glow: `0 0 20px rgba(59, 130, 246, 0.15)`,
    },
    neutral: {
      background: isDark ? 'rgba(148, 163, 184, 0.08)' : 'rgba(100, 116, 139, 0.06)',
      border: isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(100, 116, 139, 0.12)',
      iconColor: isDark ? 'rgba(148, 163, 184, 0.9)' : 'rgba(100, 116, 139, 0.8)',
      glow: 'none',
    },
  };
  return variants[variant];
};

const getSizeStyles = (size: 'small' | 'medium' | 'large') => {
  const sizes = {
    small: { container: 40, icon: 18, borderRadius: 10 },
    medium: { container: 52, icon: 24, borderRadius: 12 },
    large: { container: 64, icon: 28, borderRadius: 14 },
  };
  return sizes[size];
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $background: string;
  $border: string;
  $glass: string;
  $size: number;
  $borderRadius: number;
  $glowOnHover: boolean;
  $clickable: boolean;
  $active: boolean;
}

const StyledIconContainer = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(
  ({ $background, $border, $glass, $size, $borderRadius, $glowOnHover, $clickable, $active }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: $size,
    height: $size,
    borderRadius: $borderRadius,
    background: $background,
    border: `1px solid ${$border}`,
    position: 'relative',
    cursor: $clickable ? 'pointer' : 'default',
    transition: 'all 0.2s ease-in-out',
    boxShadow: $active ? $glass : 'none',

    '&:hover': {
      transform: $clickable ? 'scale(1.05)' : 'none',
      boxShadow: $glowOnHover ? $glass : 'none',
    },
  }),
);

const StyledWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
});

const StatusDotWrapper = styled(Box)({
  position: 'absolute',
  bottom: -2,
  right: -2,
});

// ============================================================================
// Component
// ============================================================================

export const WorkflowStep = forwardRef<HTMLDivElement, WorkflowStepProps>(
  (
    {
      icon,
      title,
      subtitle,
      variant = 'neutral',
      status,
      statusPulse = false,
      size = 'medium',
      glowOnHover = true,
      onClick,
      active = false,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const variantStyles = getVariantStyles(variant, isDark);
    const sizeStyles = getSizeStyles(size);

    return (
      <StyledWrapper ref={ref} {...props}>
        <Box
          sx={{
            position: 'relative',
          }}
        >
          <StyledIconContainer
            $background={variantStyles.background}
            $border={variantStyles.border}
            $glass={variantStyles.glow}
            $size={sizeStyles.container}
            $borderRadius={sizeStyles.borderRadius}
            $glowOnHover={glowOnHover}
            $clickable={!!onClick}
            $active={active}
            onClick={onClick}
            whileHover={onClick ? { scale: 1.05 } : undefined}
            whileTap={onClick ? { scale: 0.98 } : undefined}
          >
            <Box
              sx={{
                color: variantStyles.iconColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: sizeStyles.icon,
                '& svg': {
                  width: sizeStyles.icon,
                  height: sizeStyles.icon,
                },
              }}
            >
              {icon}
            </Box>
          </StyledIconContainer>

          {status && (
            <StatusDotWrapper>
              <StatusDot color={status} size="small" pulse={statusPulse} />
            </StatusDotWrapper>
          )}
        </Box>
        <Box
          sx={{
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: 'text.primary',
              fontSize: size === 'small' ? '0.75rem' : '0.8125rem',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: variantStyles.iconColor,
                fontWeight: 500,
                fontSize: size === 'small' ? '0.65rem' : '0.75rem',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </StyledWrapper>
    );
  },
);

WorkflowStep.displayName = 'WorkflowStep';

export default WorkflowStep;
